import { apiClient } from "../client";

export interface User {
  id: number;
  email: string;
  name: string;
  profilePicture: string | null;
  isVerified: boolean;
  isWelcomeCompleted: boolean;
  onboardingCompleted: boolean;
  organizations: Organization[];
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  url: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  street: string | null;
  postalCode: string | null;
  domain: string;
  currency: string;
  currencySymbol: string;
  timezone: string;
  timezoneOffset: string;
  dateFormat: string;
  financialYearStart: string;
  createdBy: number;
  sites: Site[];
  createdAt: string;
  updatedAt: string;
}

export interface Site {
  id: number;
  name: string;
  iconColor: string;
  systemCode: string;
  country: string;
  state: string;
  city: string;
  street: string;
  postalCode: string;
  organizationId: number;
  members: Member[];
  createdAt: string;
  updatedAt: string;
}

export interface Member {
  id: number;
  siteId: number;
  userId: number;
  email: string;
  role: string;
  status: string;
  dateJoined: string;
  invitedBy: number;
  invitationAcceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailAuthResponse {
  message: string;
  email: string;
  slug: string | null;
}

export interface VerifyEmailResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  organizations: Organization[];
}

export interface GoogleAuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  organizations: Organization[];
}

export interface UpdateProfileData {
  name?: string;
  email?: string;
  profilePicture?: string;
}

export interface UpdateProfileResponse {
  message: string;
  user: User;
}

export interface ResendCodeResponse {
  message: string;
}

export interface CreateOrganizationData {
  name: string;
  slug?: string;
  logo?: string;
  url?: string;
  country?: string;
  state?: string;
  city?: string;
  street?: string;
  postalCode?: string;
}

export interface CreateOrganizationResponse {
  message: string;
  organization: Organization;
}

export interface SlugAvailabilityResponse {
  available: boolean;
  suggestedSlug?: string;
}

export interface LoginCredentials {
  email: string;
  slug?: string;
}

export interface VerifyEmailData {
  email: string;
  code: string;
  slug?: string;
}

export interface GoogleAuthData {
  googleCode: string;
  slug?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  organizations: Organization[];
}

export interface CompleteWelcomeResponse {
  message: string;
  user: User;
}

export interface CompleteOnboardingResponse {
  message: string;
  user: User;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class AuthService {
  static async login(
    credentials: LoginCredentials
  ): Promise<EmailAuthResponse> {
    return await apiClient.post<EmailAuthResponse>("/auth/email", credentials);
  }

  static async verifyEmail(
    verifyData: VerifyEmailData
  ): Promise<VerifyEmailResponse> {
    return await apiClient.post<VerifyEmailResponse>(
      "/auth/verify",
      verifyData
    );
  }

  static async googleAuth(
    googleData: GoogleAuthData
  ): Promise<GoogleAuthResponse> {
    return await apiClient.post<GoogleAuthResponse>("/auth/google", googleData);
  }

  static async resendCode(
    email: string,
    slug?: string,
  ): Promise<ResendCodeResponse> {
    return await apiClient.post<ResendCodeResponse>("/auth/resend-code", {
      email,
      slug,
    });
  }

  static async updateProfile(
    profileData: UpdateProfileData
  ): Promise<UpdateProfileResponse> {
    return await apiClient.put<UpdateProfileResponse>(
      `/auth/user/update`,
      profileData
    );
  }

  static async createOrganization(
    organizationData: CreateOrganizationData
  ): Promise<CreateOrganizationResponse> {
    return await apiClient.post<CreateOrganizationResponse>(
      "/organizations",
      organizationData
    );
  }

  static async checkSlugAvailability(
    slug: string
  ): Promise<SlugAvailabilityResponse> {
    return await apiClient.get<SlugAvailabilityResponse>(
      `/organizations/check-slug`,
      {
        params: { slug },
      }
    );
  }

  static async logout(): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>("/auth/logout");
  }

  static async refreshToken(): Promise<RefreshTokenResponse> {
    return await apiClient.post<RefreshTokenResponse>("/auth/refresh");
  }

  static async completeWelcome(): Promise<CompleteWelcomeResponse> {
    return await apiClient.post<CompleteWelcomeResponse>(
      "/auth/complete-welcome"
    );
  }

  static async completeOnboarding(): Promise<CompleteOnboardingResponse> {
    return await apiClient.post<CompleteOnboardingResponse>(
      "/auth/complete-onboarding"
    );
  }
}
